/**
 * Sim fixture for the Team page (`/team?sim=1`): the org roster behind
 * `GET /api/org/console/members` and every write the page makes, held in
 * memory so an invite, role change or suspension shows up on the next read.
 * Resets on reload.
 *
 * Shapes:
 *   - default: you (Owner), a second Owner, two Members (one never signed
 *     in), one Invited, one Suspended.
 *   - `?team=solo`: only you. The one-Owner, empty-team state.
 *   - `?team=slow`: the default roster after a long delay (loading state).
 *   - `?team=error`: the roster read fails (error state).
 *
 * Writes answer the way yaas-api does, including its refusals: demoting or
 * suspending the last active Owner fails with LAST_ADMIN, and inviting an
 * address already on the team fails with EMAIL_IN_USE. An address containing
 * "nolink" is invited with email delivery unavailable (`delivery: "url"`), and
 * one on the `.invalid` domain is rejected, so the invite dialog's per-email
 * results can be exercised.
 */

import type {
    ClaimLinkResponse,
    InviteMemberResponse,
    OrgMember,
    OrgMemberListItem,
    OrgMemberRole
} from "@solomon-apps/shared";

const SELF_UUID = "client-user-mock";
const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
const INVITE_TTL = 7 * DAY;

export type SimTeamShape = "default" | "solo" | "slow" | "error";

/** An HTTP failure a handler answers with, carried to the fetch boundary. */
export class MockHttpError extends Error {
    constructor(
        public readonly status: number,
        public readonly body: { error: string; code?: string }
    ) {
        super(body.error);
        this.name = "MockHttpError";
    }
}

export function readSimTeamShape(): SimTeamShape {
    if (typeof window === "undefined") return "default";
    const value = new URLSearchParams(window.location.search).get("team");
    return value === "solo" || value === "slow" || value === "error" ? value : "default";
}

function iso(offsetMs: number): string {
    return new Date(Date.now() + offsetMs).toISOString();
}

function member(
    fields: Pick<OrgMemberListItem, "uuid" | "email" | "fullName" | "orgRole" | "status"> & Partial<OrgMemberListItem>
): OrgMemberListItem {
    return {
        oauthSubject: fields.status === "Invited" ? null : `sub-${fields.uuid}`,
        createdByOrgUserUuid: SELF_UUID,
        createdAt: iso(-60 * DAY),
        updatedAt: iso(-2 * DAY),
        lastLoginAt: null,
        permissions: [],
        inviteExpiresAt: null,
        ...fields
    };
}

function seedRoster(shape: SimTeamShape): OrgMemberListItem[] {
    const self = member({
        uuid: SELF_UUID,
        email: "client-admin@solomonlabs.org",
        fullName: "Client Sim Admin",
        orgRole: "Owner",
        status: "Active",
        createdByOrgUserUuid: null,
        createdAt: iso(-180 * DAY),
        lastLoginAt: iso(-10 * 60 * 1000)
    });
    if (shape === "solo") return [self];
    return [
        self,
        member({
            uuid: "team-mock-maya",
            email: "maya.okafor@clientsim.example",
            fullName: "Maya Okafor",
            orgRole: "Owner",
            status: "Active",
            createdAt: iso(-150 * DAY),
            lastLoginAt: iso(-2 * DAY - 3 * HOUR)
        }),
        member({
            uuid: "team-mock-daniel",
            email: "daniel.reyes@clientsim.example",
            fullName: "Daniel Reyes",
            orgRole: "Member",
            status: "Active",
            createdAt: iso(-90 * DAY),
            lastLoginAt: iso(-5 * HOUR)
        }),
        member({
            uuid: "team-mock-priya",
            email: "priya.natarajan@clientsim.example",
            fullName: "Priya Natarajan",
            orgRole: "Member",
            status: "Active",
            createdAt: iso(-12 * DAY),
            lastLoginAt: null
        }),
        member({
            uuid: "team-mock-invite",
            email: "sam.whitfield@clientsim.example",
            fullName: null,
            orgRole: "Member",
            status: "Invited",
            createdAt: iso(-2 * DAY),
            inviteExpiresAt: iso(5 * DAY)
        }),
        member({
            uuid: "team-mock-tom",
            email: "tom.becker@clientsim.example",
            fullName: "Tom Becker",
            orgRole: "Member",
            status: "Suspended",
            createdAt: iso(-200 * DAY),
            lastLoginAt: iso(-41 * DAY)
        })
    ];
}

let roster: OrgMemberListItem[] | null = null;
let seededShape: SimTeamShape | null = null;
let inviteSequence = 0;

function currentRoster(): OrgMemberListItem[] {
    const shape = readSimTeamShape();
    // A different `?team=` on the URL is a different fixture, not an edit.
    if (!roster || (seededShape !== shape && shape !== "slow" && shape !== "error")) {
        roster = seedRoster(shape);
        seededShape = shape;
    }
    return roster;
}

function findMember(uuid: string): OrgMemberListItem {
    const found = currentRoster().find((m) => m.uuid === uuid);
    if (!found) throw new MockHttpError(404, { error: "User not found" });
    return found;
}

function replaceMember(next: OrgMemberListItem): OrgMemberListItem {
    roster = currentRoster().map((m) => (m.uuid === next.uuid ? next : m));
    return next;
}

function asOrgMember(item: OrgMemberListItem): OrgMember {
    // Mutation responses carry no invitation lifecycle or permission state.
    return {
        uuid: item.uuid,
        email: item.email,
        fullName: item.fullName,
        oauthSubject: item.oauthSubject,
        status: item.status,
        orgRole: item.orgRole,
        createdByOrgUserUuid: item.createdByOrgUserUuid,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        lastLoginAt: item.lastLoginAt
    };
}

function claimLink(uuid: string, email: string): ClaimLinkResponse {
    return {
        claimUrl: `http://localhost:3401/claim#token=sim-${uuid}-${Date.now().toString(36)}`,
        delivery: email.includes("nolink") ? "url" : "email_and_url"
    };
}

function lastAdminError(verb: "demote" | "suspend"): MockHttpError {
    return new MockHttpError(400, {
        error: `Cannot ${verb} the last admin of this organization`,
        code: "LAST_ADMIN"
    });
}

function isLastActiveOwner(target: OrgMemberListItem): boolean {
    return (
        target.orgRole === "Owner" &&
        target.status === "Active" &&
        currentRoster().filter((m) => m.orgRole === "Owner" && m.status === "Active").length <= 1
    );
}

interface InviteBody {
    email?: unknown;
    fullName?: unknown;
    orgRole?: unknown;
}

function readInviteBody(body: unknown): { email: string; fullName: string | null; orgRole: OrgMemberRole } {
    const input = (body ?? {}) as InviteBody;
    const email = typeof input.email === "string" ? input.email.trim() : "";
    const fullName = typeof input.fullName === "string" && input.fullName.trim() ? input.fullName.trim() : null;
    const orgRole: OrgMemberRole = input.orgRole === "Owner" ? "Owner" : "Member";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.toLowerCase().endsWith(".invalid")) {
        throw new MockHttpError(400, { error: "Enter a valid email address." });
    }
    return { email, fullName, orgRole };
}

function emailTaken(email: string, exceptUuid?: string): boolean {
    const key = email.toLowerCase();
    return currentRoster().some((m) => m.uuid !== exceptUuid && m.email.toLowerCase() === key);
}

/* ── Handlers ─────────────────────────────────────────────────── */

export async function listTeam(): Promise<{
    data: OrgMemberListItem[];
    total: number;
    limit: number;
    offset: number;
    page: number;
}> {
    const shape = readSimTeamShape();
    if (shape === "error") throw new MockHttpError(500, { error: "Internal Server Error" });
    if (shape === "slow") await new Promise((resolve) => setTimeout(resolve, 4000));
    const data = currentRoster();
    return { data, total: data.length, limit: 200, offset: 0, page: 1 };
}

export function inviteTeamMember(body: unknown): InviteMemberResponse {
    const { email, fullName, orgRole } = readInviteBody(body);
    if (emailTaken(email)) {
        throw new MockHttpError(409, {
            error: "Org user with this email already exists",
            code: "EMAIL_IN_USE"
        });
    }
    inviteSequence += 1;
    const created = member({
        uuid: `team-mock-new-${inviteSequence}`,
        email,
        fullName,
        orgRole,
        status: "Invited",
        createdAt: iso(0),
        updatedAt: iso(0),
        inviteExpiresAt: iso(INVITE_TTL)
    });
    roster = [...currentRoster(), created];
    return { orgUser: asOrgMember(created), ...claimLink(created.uuid, email) };
}

export function patchTeamMember(uuid: string, body: unknown): OrgMember {
    const target = findMember(uuid);
    const input = (body ?? {}) as { orgRole?: unknown; status?: unknown; fullName?: unknown };
    let next = { ...target, updatedAt: iso(0) };
    if (input.orgRole === "Owner" || input.orgRole === "Member") {
        if (input.orgRole === "Member" && isLastActiveOwner(target)) throw lastAdminError("demote");
        next = { ...next, orgRole: input.orgRole };
    }
    if (input.status === "Active" || input.status === "Suspended") {
        if (target.status === "Invited") {
            throw new MockHttpError(400, { error: "Cannot change status of an invited user" });
        }
        if (input.status === "Suspended" && isLastActiveOwner(target)) throw lastAdminError("suspend");
        next = { ...next, status: input.status };
    }
    if (typeof input.fullName === "string") next = { ...next, fullName: input.fullName.trim() || null };
    return asOrgMember(replaceMember(next));
}

export function resetTeamMemberPassword(uuid: string): ClaimLinkResponse {
    const target = findMember(uuid);
    if (target.status !== "Active") {
        throw new MockHttpError(400, { error: `Cannot reset password for user with status ${target.status}` });
    }
    return claimLink(uuid, target.email);
}

export function regenerateTeamMemberClaim(uuid: string): ClaimLinkResponse {
    const target = findMember(uuid);
    if (target.status !== "Invited") {
        throw new MockHttpError(400, { error: `Cannot regenerate claim url for user with status ${target.status}` });
    }
    replaceMember({ ...target, inviteExpiresAt: iso(INVITE_TTL), updatedAt: iso(0) });
    return claimLink(uuid, target.email);
}

export function fixTeamMemberInvite(uuid: string, body: unknown): InviteMemberResponse {
    const target = findMember(uuid);
    if (target.status !== "Invited") {
        throw new MockHttpError(400, { error: "User already claimed; email is immutable" });
    }
    const { email, fullName, orgRole } = readInviteBody(body);
    if (emailTaken(email, uuid)) {
        throw new MockHttpError(409, {
            error: "Org user with this email already exists",
            code: "EMAIL_IN_USE"
        });
    }
    const next = replaceMember({
        ...target,
        email,
        fullName,
        orgRole,
        inviteExpiresAt: iso(INVITE_TTL),
        updatedAt: iso(0)
    });
    return { orgUser: asOrgMember(next), ...claimLink(uuid, email) };
}

export function cancelTeamMemberInvite(uuid: string): { success: boolean } {
    const target = findMember(uuid);
    if (target.status !== "Invited") {
        throw new MockHttpError(400, { error: `Cannot cancel invitation for user with status ${target.status}` });
    }
    roster = currentRoster().filter((m) => m.uuid !== uuid);
    return { success: true };
}
